import { useCallback } from 'react';
import { FileRejection } from 'react-dropzone';
import { SerializedError } from '@reduxjs/toolkit';
import {
  getDurationFromAudioFile,
  parseDuration,
  validateAudioDuration,
} from '../helpers/duration';
import useErrorMessage from './useErrorMessage';

type PropsType = {
  onSuccess: (audioFile: File) => void;
  onError: (audioFile: File, error: SerializedError, duration?: number) => void;
};

const useUploadAudioValidation = ({ onSuccess, onError }: PropsType) => {
  const getErrorMessage = useErrorMessage();

  const handleError = useCallback(
    (file: File, errorCode: string, duration?: number) => {
      const message = getErrorMessage(errorCode);
      onError(file, { message }, parseDuration(duration));
    },
    [getErrorMessage, onError]
  );

  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }
      const duration = await getDurationFromAudioFile(file);

      try {
        validateAudioDuration(duration);

        onSuccess(file);
      } catch (errorCode) {
        handleError(file, errorCode as string, duration);
      }
    },
    [onSuccess, handleError]
  );

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const fileRejection = fileRejections[0];
      if (!fileRejection) {
        return;
      }

      const { file, errors } = fileRejection;
      const fileError = errors[0];
      if (!fileError) {
        return;
      }

      handleError(file, fileError.code);
    },
    [handleError]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        onDropAccepted(acceptedFiles);
      } else {
        onDropRejected(fileRejections);
      }
    },
    [onDropAccepted, onDropRejected]
  );

  return {
    onDrop,
  };
};

export default useUploadAudioValidation;
